import {
    createFailure,
    NamedProperty,
    validationFailed,
    ValidationResult,
    validationSuccessful
} from "sprint-common-web";

import { DynamicSubtitleType } from "../../common/types/DynamicSubtitleType";
import { OrderAudioProxyType } from "../../orders/common/types/OrderAudioProxyType";

export interface SystemPlatformValidator {
    validate(item: SystemPlatformValidationRequest): ValidationResult;
}

export interface SystemPlatformValidationRequest {
    audioProxyType: OrderAudioProxyType | undefined;
    muxSubtitleType: DynamicSubtitleType | undefined;
    copySubtitleType: DynamicSubtitleType | undefined;
    muxSpecificationId: string | undefined;
    deliveryEndpoint: NamedProperty | undefined;
    buzzIds: string[] | undefined;
    shreqIds: string[] | undefined;
    movieSpecificationId: string | undefined;
    tvSpecificationId: string | undefined;
}

export const systemPlatformValidator = (): SystemPlatformValidator => {
    const validate = (item: SystemPlatformValidationRequest): ValidationResult => {
        const validationRules = [
            validateSubtitleTypes(item.muxSubtitleType, item.copySubtitleType),
            validateMuxSpecification(item.muxSpecificationId),
            validateAudioProxyType(item.audioProxyType),
            validateDeliveryEndpoint(item.deliveryEndpoint),
            validateBuzzIds(item.buzzIds),
            validateShreqIds(item.shreqIds),
            validateMovieAndTvSpecification(item.movieSpecificationId, item.tvSpecificationId)
        ];

        const failureArray = validationRules.flatMap((result) => result.failures);

        if (failureArray.length) return validationFailed(failureArray);

        return validationSuccessful();
    };

    const validateSubtitleTypes = (
        muxSubtitleType: DynamicSubtitleType | undefined,
        copySubtitleType: DynamicSubtitleType | undefined
    ): ValidationResult => {
        if (muxSubtitleType || copySubtitleType) return validationSuccessful();

        const failure = createFailure(
            "Mux subtitle type or sidecar subtitle type are not selected",
            "no-subtitle-type"
        );

        return validationFailed([failure]);
    };

    const validateMuxSpecification = (muxSpecificationId: string | undefined): ValidationResult => {
        if (muxSpecificationId) return validationSuccessful();

        const failure = createFailure("Mux specification is not selected", "no-mux-specification");

        return validationFailed([failure]);
    };

    const validateAudioProxyType = (audioProxyType: OrderAudioProxyType | undefined): ValidationResult => {
        if (audioProxyType) return validationSuccessful();

        const failure = createFailure("Audio proxy type is not selected", "no-audio-proxy-type");

        return validationFailed([failure]);
    };

    const validateDeliveryEndpoint = (deliveryEndpoint: NamedProperty | undefined): ValidationResult => {
        if (deliveryEndpoint) return validationSuccessful();

        const failure = createFailure("Delivery endpoint is not selected", "no-delivery-endpoint");

        return validationFailed([failure]);
    };

    const validateBuzzIds = (buzzIds: string[] | undefined): ValidationResult => {
        if (buzzIds?.length) return validationSuccessful();

        const failure = createFailure("Buzz Ids are not provided", "no-buzz-ids");

        return validationFailed([failure]);
    };

    const validateShreqIds = (shreqIds: string[] | undefined): ValidationResult => {
        if (shreqIds?.length) return validationSuccessful();

        const failure = createFailure("Shreq Ids are not provided", "no-shreq-ids");

        return validationFailed([failure]);
    };

    const validateMovieAndTvSpecification = (
        movieSpecificationId: string | undefined,
        tvSpecificationId: string | undefined
    ): ValidationResult => {
        if (movieSpecificationId || tvSpecificationId) return validationSuccessful();

        const failure = createFailure(
            "Encode specification for movie or tv is not selected",
            "no-movie-and-tv-specfication"
        );

        return validationFailed([failure]);
    };

    return {
        validate
    };
};
